import { TObject, Type } from '@sinclair/typebox';
import {
  CreateAvailabilityObject,
  CreateAvailabilitySchema,
  SpecificDateSlotRequestSchema,
  TimeSlotRequestObject,
  TimeSlotRequestSchema,
} from './availabilities';
import { isAvailabilityActivationDay } from './availability-recurrence-rule';
import { FormVisibilityPredicate } from './form-visibility';

const requestFields = Type.Required(CreateAvailabilitySchema).properties;
const specificDateFields = Type.Required(SpecificDateSlotRequestSchema).properties;
const recurringVisibility = { field: 'slots', predicate: FormVisibilityPredicate.HAS_ITEMS };

export const AVAILABILITY_WEEKLY_FORM_GROUP = 'availability_weekly';
export const AVAILABILITY_WEEKLY_FORM_FIELDS = ['slots', 'recurrence_rrule', 'start', 'end'] as const;

const specificDateFormSchema = Type.Union([
  Type.Object({
    ...SpecificDateSlotRequestSchema.properties,
    team_members_overridden: Type.Optional(Type.Literal(false)),
  }),
  Type.Object({
    ...SpecificDateSlotRequestSchema.properties,
    team_members_overridden: Type.Literal(true),
    team_member_ids: Type.Array(specificDateFields.team_member_ids.items, {
      ...specificDateFields.team_member_ids,
      minItems: 1,
    }),
  }),
]);

/**
 * Un seul formulaire : les dates peuvent suffire sans aucun jour hebdomadaire.
 * La période et la récurrence masquées restent un brouillon RHF, même incomplet ;
 * le mapper les exclut alors de la requête. Les validations des champs actifs restent strictes.
 */
export const buildAvailabilityFormSchema = (hasWeeklySlots: boolean): TObject => {
  const periodMeta = { visible_when: recurringVisibility };
  const specificDates = Type.Array(specificDateFormSchema, {
    ...requestFields.specific_dates,
    minItems: hasWeeklySlots ? 0 : 1,
  });
  const schema = Type.Object({
    ...CreateAvailabilitySchema.properties,
    slots: Type.Array(TimeSlotRequestSchema, { ...requestFields.slots, minItems: 0 }),
    recurrence_rrule: hasWeeklySlots
      ? Type.Optional(
          Type.String({
            ...requestFields.recurrence_rrule,
            meta: { ...periodMeta, input_type: 'availability_recurrence_selector' },
          }),
        )
      : Type.Optional(Type.Unknown({ meta: periodMeta })),
    start: hasWeeklySlots
      ? Type.String({ ...requestFields.start, meta: { ...periodMeta, default_today: true } })
      : Type.Optional(Type.Unknown({ meta: { ...periodMeta, default_today: true } })),
    end: hasWeeklySlots
      ? Type.Optional(Type.String({ ...requestFields.end, meta: { ...periodMeta, must_be_after: 'start' } }))
      : Type.Optional(Type.Unknown({ meta: periodMeta })),
    specific_dates: hasWeeklySlots ? Type.Optional(specificDates) : specificDates,
  });
  for (const field of AVAILABILITY_WEEKLY_FORM_FIELDS) {
    schema.properties[field].meta = {
      ...schema.properties[field].meta,
      group: AVAILABILITY_WEEKLY_FORM_GROUP,
    };
  }
  return schema;
};

export const availabilityFormToRequest = (values: CreateAvailabilityObject): CreateAvailabilityObject => {
  const { start, end, recurrence_rrule, specific_dates, ...common } = values;
  return {
    ...common,
    ...(values.slots.length > 0 ? { start, end, recurrence_rrule } : {}),
    specific_dates: specific_dates?.map((slot) => {
      const { team_member_ids, ...rest } = slot;
      return {
        ...rest,
        team_members_overridden: slot.team_members_overridden === true,
        ...(slot.team_members_overridden === true ? { team_member_ids } : {}),
      };
    }),
  };
};

const wallDayPrefix = (value: unknown): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;

/**
 * Créneaux hebdo qu'une date ponctuelle posée ce jour-là REMPLACE (règle backend : un jour qui porte
 * une date n'ouvre que les dates de son planning). Vide quand le jour de semaine n'a aucun créneau,
 * tombe hors de la période du planning ou dans une semaine inactive de sa règle d'activation
 * (une semaine sur deux, Nème semaine du mois) : ce jour-là, il n'y a rien à remplacer.
 */
export const weeklySlotsReplacedOnDate = (
  date: string,
  planning: { slots?: readonly TimeSlotRequestObject[]; start?: unknown; end?: unknown; recurrence_rrule?: unknown },
): TimeSlotRequestObject[] => {
  const day = wallDayPrefix(date);
  if (day === null) return [];
  const startDay = wallDayPrefix(planning.start);
  const endDay = wallDayPrefix(planning.end);
  if ((startDay !== null && day < startDay) || (endDay !== null && day > endDay)) return [];
  // Sans début, la règle n'a pas d'ancre : le backend en exige un dès qu'il y a des créneaux hebdo.
  const recurrenceRrule = typeof planning.recurrence_rrule === 'string' ? planning.recurrence_rrule : undefined;
  if (startDay !== null && !isAvailabilityActivationDay(recurrenceRrule, startDay, day)) return [];
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return (planning.slots ?? [])
    .filter((slot) => slot.day === weekday && slot.end > slot.start)
    .sort((left, right) => left.start - right.start);
};
